import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from './contexts/OrganizationContext';
import Page1 from './pages/Page1';
import Page2 from './pages/Page2';
import Page3 from './pages/Page3';
import Page4 from './pages/Page4';
import Page5 from './pages/Page5';
import Page6 from './pages/Page6';
import ThankYouPage from './pages/ThankYouPage';
import ProgressIndicator from './components/ProgressIndicator';
import { generateTaxFormPDF, generatePDFFilename } from './utils/pdfGenerator';
import { completeSubmission } from './services/supabaseService';
import { sendAdminEmail, sendClientEmail } from './services/emailService';

function MultiPageTaxForm() {
  const { i18n } = useTranslation();
  const { organization, loading: orgLoading } = useOrganization();
  const [language, setLanguage] = useState('en');
  const [currentPage, setCurrentPage] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState(null);

  // Set dynamic document title based on organization
  useEffect(() => {
    document.title = organization?.name
      ? `${organization.name} - Tax Form 2025`
      : 'Tax Form 2025';
  }, [organization?.name]);

  // Set dynamic favicon based on organization favicon
  useEffect(() => {
    if (organization?.favicon_url) {
      // Use dedicated favicon for better display
      const faviconUrl = organization.favicon_url;
      
      // Update favicon
      const favicon = document.querySelector("link[rel*='icon']");
      if (favicon) {
        favicon.href = faviconUrl;
      }
      
      // Update apple touch icon
      const appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']");
      if (appleTouchIcon) {
        appleTouchIcon.href = faviconUrl;
      }
    }
  }, [organization?.favicon_url]);

  const [formData, setFormData] = useState({
    // Page 1 - Personal Information
    taxpayerFirstName: '',
    taxpayerMiddleName: '',
    taxpayerLastName: '',
    dateOfBirth: '',
    filingStatus: '',
    incomeSource: '',
    streetAddress: '',
    streetAddress2: '',
    city: '',
    state: '',
    postalCode: '',
    phoneNumber: '',
    email: '',
    ssn: '',
    driversLicense: '',
    idExpirationDate: '',
    hasMarketplaceInsurance: '',
    wantsHealthInsurance: '',
    hasLifeInsurance: '',
    wantsLifeInsurance: '',
    homeOwnership: '',
    wantsToBuyHome: '',
    
    // Spouse Information
    spouseFirstName: '',
    spouseMiddleName: '',
    spouseLastName: '',
    spouseDateOfBirth: '',
    spouseSsn: '',
    spouseDriversLicense: '',
    spouseIdExpirationDate: '',
    
    // Dependents
    claimingDependents: '',
    numberOfDependents: '1',
    dependents: [],
    
    // Page 2 - Qualifying Dependent Questions
    allDependentsCitizens: '',
    onlyPersonClaiming: '',
    filingJointReturn: '',
    relatedToYou: '',
    meetsAgeRequirement: '',
    livesWithYou: '',
    financiallySupport: '',
    hasUtilityBill: '',
    dependentIncomeLessThan4200: '',
    childCareExpenses: '',
    
    // Page 3 - Self Employment Acknowledgment
    householdEmployeeAcknowledged: false,
    recordKeepingAcknowledged: false,
    
    // Page 4 - Schedule C Worksheet (MUST initialize with full template to persist to database)
    scheduleC: {
      businessName: '',
      businessAddress: '',
      firstYearInBusiness: '',
      federalId: '',
      madePaymentsRequiring1099: '',
      filed1099: '',
      totalGrossIncome: '',
      
      // Retail Inventory (conditional)
      isRetailBusiness: '',
      beginningInventory: '',
      merchandisePurchased: '',
      costOfLabor: '',
      materialsSupplies: '',
      otherDirectSalesCosts: '',
      endingInventory: '',
      
      // Business Expenses
      advertising: '',
      commissionsAndFees: '',
      contractLabor: '',
      insuranceOtherThanHealth: '',
      healthInsuranceSelf: '',
      healthInsuranceEmployees: '',
      mortgageInterestBusiness: '',
      otherInterestPaid: '',
      professionalFees: '',
      officeExpenses: '',
      rentOnBusinessProperty: '',
      equipmentRentals: '',
      telephone: '',
      telephonePercentBusiness: '',
      repairsAndMaintenance: '',
      suppliesNotIncludedAbove: '',
      realEstateTaxesBusiness: '',
      otherTaxesPayroll: '',
      travel: '',
      mealsAndEntertainment: '',
      utilities: '',
      wagesW2Issued: '',
      bankAndCCCharges: '',
      tools: '',
      uniformsLogo: '',
      licenseDues: '',
      otherExpenses: [],
      
      // Mileage
      businessMileage: '',
      personalMileage: '',
      hasEvidenceForMileage: '',
      isEvidenceWritten: '',
      
      // Equipment Purchases
      purchasedMajorEquipment: '',
      equipment: [],
      
      // Home Office
      hasHomeOffice: '',
      officeSquareFeet: '',
      homeSquareFeet: '',
      realEstateTaxesHome: '',
      mortgageInterestOrRent: '',
      homeownersInsurance: '',
      utilitiesLightsGas: ''
    },
    
    // Page 5 - Fees Summary
    feesName: '',
    feesSsn: '',
    
    // Page 6 - Disclosure
    disclosureTaxpayerFirstName: '',
    disclosureTaxpayerMiddleName: '',
    disclosureTaxpayerLastName: '',
    signature: '',
    signatureDate: '',
    uploadedFiles: []
  });

  const changeLanguage = (lng) => {
    setLanguage(lng);
    i18n.changeLanguage(lng);
  };

  // Determine which pages to show based on conditional logic
  const getPageSequence = () => {
    const pages = [1]; // Page 1 always shows
    
    // Page 2 shows only if claiming dependents
    if (formData.claimingDependents === 'Yes') {
      pages.push(2);
    }
    
    // Page 3 shows only if income source includes Self Employment or 1099
    const incomeSource = formData.incomeSource || '';
    if (incomeSource.includes('Self Employment') || incomeSource.includes('1099')) {
      pages.push(3);
    }
    
    // Page 4 shows only if Page 3 showed (self-employed)
    if (pages.includes(3)) {
      pages.push(4);
    }
    
    // Pages 5 and 6 always show
    pages.push(5, 6);
    
    return pages;
  };

  const pageSequence = getPageSequence();
  const totalPages = pageSequence.length;
  const currentPageIndex = pageSequence.indexOf(currentPage);

  const handleNext = () => {
    const nextIndex = currentPageIndex + 1;
    if (nextIndex < pageSequence.length) {
      setCurrentPage(pageSequence[nextIndex]);
      window.scrollTo(0, 0);
    } else {
      // This shouldn't happen as Page 6 handles submission
      handleSubmit();
    }
  };

  const handleBack = () => {
    const prevIndex = currentPageIndex - 1;
    if (prevIndex >= 0) {
      setCurrentPage(pageSequence[prevIndex]);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async (signatureData = null) => {
    // Prevent double submission
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      // Merge signature and organization_id into formData
      const finalFormData = {
        ...formData,
        ...(signatureData && { signature: signatureData }),
        organization_id: organization?.id // Add organization ID
      };
      
      console.log('Starting submission process...');
      console.log('Signature in formData:', finalFormData.signature ? 'YES - Length: ' + finalFormData.signature.length : 'NO');
      console.log('Schedule C data present:', finalFormData.scheduleC ? 'YES' : 'NO');
      console.log('Schedule C business name:', finalFormData.scheduleC?.businessName || '(empty)');
      
      // Step 1: Submit to Supabase FIRST to get confirmation number
      console.log('Submitting to Supabase...');
      const result = await completeSubmission(
        finalFormData,
        finalFormData.uploadedFiles || [],
        null, // Don't upload PDF yet, we need confirmation number first
        null,
        language
      );
      
      if (!result.success) {
        throw new Error(result.error || 'Submission failed');
      }
      
      console.log('Submission successful! Confirmation:', result.confirmationNumber);
      
      // Step 2: Generate PDF WITH confirmation number
      console.log('Generating PDF with confirmation number...');
      const pdf = await generateTaxFormPDF(finalFormData, language, result.confirmationNumber, organization);
      const filename = generatePDFFilename(finalFormData, result.confirmationNumber);
      const pdfBlob = pdf.output('blob');
      console.log('PDF generated as blob, size:', pdfBlob.size);
      
      // Step 3: Upload PDF to Supabase
      console.log('Uploading PDF to Supabase...');
      const { uploadPDF } = await import('./services/supabaseService');
      await uploadPDF(pdfBlob, result.formId, finalFormData, result.confirmationNumber, filename);
      
      // Step 4: Send emails (don't block on this - continue even if emails fail)
      console.log('Sending emails...');
      try {
        // Get submission date/time in user's timezone
        const submissionDateTime = new Date().toLocaleString('en-US', {
          dateStyle: 'full',
          timeStyle: 'short'
        });
        
        // Send to office admin with PDF attachment and file download links
        const adminEmailResult = await sendAdminEmail(
          organization,
          finalFormData, 
          pdfBlob,
          finalFormData.uploadedFiles || [], // Pass uploaded files
          result.confirmationNumber,
          submissionDateTime,
          result.filePaths || [] // Pass file paths for generating download links
        );
        console.log('Admin email:', adminEmailResult.success ? 'Sent successfully' : 'Failed - ' + adminEmailResult.error);
        
        // Send thank you email to client
        const clientEmailResult = await sendClientEmail(
          organization,
          finalFormData, 
          result.confirmationNumber,
          submissionDateTime
        );
        console.log('Client email:', clientEmailResult.success ? 'Sent successfully' : 'Failed - ' + clientEmailResult.error);
      } catch (emailError) {
        console.error('Error sending emails (non-critical):', emailError);
        // Don't fail the submission if emails fail
      }
      
      // Step 5: Navigate to thank you page (NO PDF download)
      setConfirmationNumber(result.confirmationNumber);
      setCurrentPage('thank-you');
      window.scrollTo(0, 0);
      
    } catch (error) {
      console.error('Error during submission:', error);
      alert(
        'There was an error submitting your form. Please try again or contact support if the problem persists.\n\n' +
        'Error: ' + error.message
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (updates) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  };

  if (currentPage === 'thank-you') {
    return <ThankYouPage confirmationNumber={confirmationNumber} />;
  }

  return (
    <div className="min-h-screen bg-[#E8E8ED]">
      {/* Orange Header Bar */}
      <div className="w-full h-1 bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-400"></div>
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Admin Portal Button - Top Right - Only on Page 1 */}
        {currentPage === 1 && (
          <div className="flex justify-end mb-4">
            <a
              href="/admin/login"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-medium rounded-full shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Admin Portal
            </a>
          </div>
        )}
        {/* Logo */}
        <div className="text-center mb-2">
          {orgLoading ? (
            <div className="h-48 md:h-72 flex items-center justify-center">
              <div className="animate-pulse text-gray-400">Loading...</div>
            </div>
          ) : organization?.logo_url ? (
            <img 
              src={organization.logo_url} 
              alt={organization.name} 
              className="h-48 md:h-72 mx-auto object-contain"
              style={{ display: 'block' }}
              onError={(e) => {
                console.error('Logo failed to load:', organization.logo_url);
                e.target.style.display = 'none';
                const parent = e.target.parentElement;
                parent.innerHTML = `<h1 class="text-2xl md:text-4xl font-bold text-gray-800">${organization?.name || 'Tax Services'}</h1>`;
              }}
              onLoad={() => {
                console.log('Logo loaded successfully:', organization.logo_url);
              }}
            />
          ) : (
            <div className="h-48 md:h-72 flex items-center justify-center">
              <h1 className="text-2xl md:text-4xl font-bold text-gray-800">
                {organization?.name || 'Tax Services'}
              </h1>
            </div>
          )}
        </div>

        {/* Language Selector - Top Right */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => changeLanguage(language === 'en' ? 'es' : 'en')}
            className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 bg-white px-3 py-2 rounded border border-gray-300"
          >
            <span className="text-lg">🇺🇸</span>
            <span>{language === 'en' ? 'English (US)' : 'Español'}</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Form Container with White Background */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
          {/* Progress Indicator Inside Card */}
          <div className="px-8 pt-6 pb-4 border-b border-gray-200">
            <ProgressIndicator 
              currentPage={currentPageIndex + 1} 
              totalPages={totalPages} 
            />
          </div>

          {/* Form Content */}
          <div className="px-8 py-6">
            {currentPage === 1 && (
              <Page1 
                formData={formData}
                updateFormData={updateFormData}
                onNext={handleNext}
              />
            )}
            
            {currentPage === 2 && (
              <Page2 
                formData={formData}
                updateFormData={updateFormData}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
            
            {currentPage === 3 && (
              <Page3 
                formData={formData}
                updateFormData={updateFormData}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
            
            {currentPage === 4 && (
              <Page4 
                formData={formData}
                updateFormData={updateFormData}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
            
            {currentPage === 5 && (
              <Page5 
                formData={formData}
                updateFormData={updateFormData}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
            
            {currentPage === 6 && (
              <Page6 
                formData={formData}
                updateFormData={updateFormData}
                onSubmit={(signatureData) => handleSubmit(signatureData)}
                onBack={handleBack}
                isSubmitting={isSubmitting}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MultiPageTaxForm;